import type { PayloadAction} from '@reduxjs/toolkit';
import {createSlice} from '@reduxjs/toolkit';
import type {AddItemsToGridProps, GridStorageTypes} from '@Components/base-grid';
import type {GridPMWStockMediaItemStorage} from '@Components/base-grid/components/grid-pmwstockmedia-item/grid-pmw-stock-media-item';
import {arraysEqual, insert} from '@Utils/array.util';
import {loadStoredItemsPerPage} from '@Panels/manage-pmw-stock-panels/pmw-stock-panels-helper';
import {InfoTab} from './manage-pmw-stock-panels-types';

type GridsStorage = Record<string, GridStorageTypes>;
interface ManagePMWSavedStateProps {
  savedGridsHashmap: GridsStorage;
  gridSelectionGroupHashmap: SelectionGroupHashmap;
}

interface GridSelectionGroupStorage {
  savedGridsHashmap: Record<string, null>;
  maxSelectionNumber: number;
  selectedItemIdHashmap: Record<string, number>;
}

type SelectionGroupHashmap = Record<string, GridSelectionGroupStorage>;

interface GridPaginationState {
  currentPage: number;
  totalItems: number;
}

interface ManagePmwStockPanelState {
  PMWStockChanges: Record<string, Partial<GridPMWStockMediaItemStorage>>;
  savedGridsHashmap: GridsStorage;
  gridSelectionGroupHashmap: SelectionGroupHashmap;
  gridDefaultInfoTab: InfoTab;
  gridPagination: Record<string, GridPaginationState>;
  itemsPerPage: number;
}

const initialState: ManagePmwStockPanelState = {
  PMWStockChanges: {},
  savedGridsHashmap: {},
  gridSelectionGroupHashmap: {},
  gridDefaultInfoTab: InfoTab.DESCRIPTION,
  gridPagination: {},
  itemsPerPage: loadStoredItemsPerPage(),
};

const getGridPaginationState = (state: ManagePmwStockPanelState, gridId: string): GridPaginationState => {
  return state.gridPagination[gridId] ?? {currentPage: 1, totalItems: 0};
};

interface AddChangeToLogItemProps {
  changeToLog: Partial<GridPMWStockMediaItemStorage>;
  itemToChange: Partial<GridPMWStockMediaItemStorage>;
}

interface AddLazyGridActionProps {
  id: string;
  gridSelectionGroupId?: string;
  maxSelectionNumber?: number;
}

const doesGridExistInStore = (state: ManagePMWSavedStateProps, gridId: string): boolean => {
  return state.savedGridsHashmap[gridId] !== undefined;
};

const addGridToSelecctionGroup = (gridId: string, gridSelectionGroupId: string | undefined, maxSelectionNumber: number | undefined, state: ManagePMWSavedStateProps): void => {
  const selectionGroupId = gridSelectionGroupId ?? gridId;

  if (!state.gridSelectionGroupHashmap[selectionGroupId]) {
    state.gridSelectionGroupHashmap[selectionGroupId] = {
      savedGridsHashmap: {},
      maxSelectionNumber: maxSelectionNumber ?? 0,
      selectedItemIdHashmap: {},
    };
  }
  state.gridSelectionGroupHashmap[selectionGroupId].savedGridsHashmap[gridId] = null;
};

export const managePmwStockPanelSlice = createSlice({
  name: 'ManagePMWStockPanel',
  initialState,
  reducers: {
    addLazyGridManagePMW: (state, action: PayloadAction<AddLazyGridActionProps>) => {
      if (!doesGridExistInStore(state, action.payload.id)) {
        state.savedGridsHashmap[action.payload.id] = {
          items: [],
          requestNum: 1,
        };

        addGridToSelecctionGroup(action.payload.id, action.payload?.gridSelectionGroupId, action.payload?.maxSelectionNumber, state);
      }
    },
    addItemsToGridManagePMW: (state, action: PayloadAction<AddItemsToGridProps>) => {
      const existingGridItems = state.savedGridsHashmap[action.payload.id].items;
      state.savedGridsHashmap[action.payload.id].items = insert(existingGridItems, action.payload.index ?? existingGridItems.length, action.payload.items);
    },
    addChangeToLog: (state, action: PayloadAction<AddChangeToLogItemProps>) => {
      const {changeToLog, itemToChange} = action.payload;
      if (changeToLog.id) {
        if (!state.PMWStockChanges[changeToLog.id]) {
          state.PMWStockChanges[changeToLog.id] = {};
        }

        state.PMWStockChanges[changeToLog.id].changes ??= {};

        state.PMWStockChanges[changeToLog.id].original ??= itemToChange;

        if (state.PMWStockChanges[changeToLog.id].original) {
          const originalItem = state.PMWStockChanges[changeToLog.id].original as Partial<GridPMWStockMediaItemStorage>;
          const changeToLogKeys = Object.keys(changeToLog);

          let isChangedItemEqualToOriginal = changeToLogKeys.every((key) => {
            if (key !== 'keywords') {
              return originalItem[key] !== null && originalItem[key] !== undefined && originalItem[key] === changeToLog[key];
            }
            return true;
          });

          if (changeToLog.keywords) {
            isChangedItemEqualToOriginal = isChangedItemEqualToOriginal && arraysEqual(changeToLog.keywords, originalItem.keywords ?? []);
          }

          if (isChangedItemEqualToOriginal) {
            delete state.PMWStockChanges[changeToLog.id];
          } else {
            state.PMWStockChanges[changeToLog.id].changes = {...state.PMWStockChanges[changeToLog.id].changes, ...changeToLog} as Partial<GridPMWStockMediaItemStorage>;
          }
        }
      }
    },
    clearGridItemsManagePMW: (state) => {
      state.PMWStockChanges = {};
      state.gridSelectionGroupHashmap = {};
      state.savedGridsHashmap = {};
      state.gridPagination = {};
    },
    setGridDefaultInfoTab: (state, action: PayloadAction<InfoTab>) => {
      state.gridDefaultInfoTab = action.payload;
    },
    setGridPage: (state, action: PayloadAction<{gridId: string; page: number}>) => {
      state.gridPagination[action.payload.gridId] = {
        ...getGridPaginationState(state, action.payload.gridId),
        currentPage: action.payload.page,
      };
    },
    setGridTotalItems: (state, action: PayloadAction<{gridId: string; totalItems: number}>) => {
      state.gridPagination[action.payload.gridId] = {
        ...getGridPaginationState(state, action.payload.gridId),
        totalItems: action.payload.totalItems,
      };
    },
    setItemsPerPage: (state, action: PayloadAction<number>) => {
      state.itemsPerPage = action.payload;
    },
  },
});

export const {addLazyGridManagePMW, addChangeToLog, clearGridItemsManagePMW, setGridDefaultInfoTab, setGridPage, setGridTotalItems, setItemsPerPage} =
  managePmwStockPanelSlice.actions;
export const managePmwStockReducer = managePmwStockPanelSlice.reducer;
